import { computed } from "vue";
import { OVERLAY_LOCALES } from "@/core/constants/language-and-currency";
import { localizeInternalUrl } from "@/core/utilities/common";
import FeatureFlags from "@/core/utilities/featureFlags";
import { useLanguages } from "./useLanguages";
import { useThemeContext } from "./useThemeContext";
import type { LanguageAndCurrency } from "@/core/types/defaultLanguageCurrency";
import type { LocalizeInternalUrlOptionsType } from "@/core/utilities/common";

const SOFTWARE_PAGES_PATH = "/software-pages";

/**
 * Binds {@link localizeInternalUrl} to the active locale and to this storefront's own hostnames,
 * so no call site has to hardcode a locale list or a domain.
 */
export function useLocalizedUrl() {
  const { currentLocale, defaultLocale, supportedLocales } = useLanguages();
  const { themeContext } = useThemeContext();

  // The CMS and Algolia emit absolute URLs built from whichever origin they were configured with,
  // which differs per environment (prod, prep) and per ccTLD — every storefront host must count as
  // same-site or normalization silently no-ops outside production.
  const sameSiteHostnames = computed<string[]>(() => {
    const featureFlags = new FeatureFlags(themeContext.value?.storeSettings?.modules ?? []);
    const ccTLDsValue = (featureFlags.getValue("Thorlabs.Commerce", "ccTLDs") as string) || "";

    let domains: Record<string, LanguageAndCurrency> = {};
    try {
      domains = ccTLDsValue ? (JSON.parse(ccTLDsValue) as Record<string, LanguageAndCurrency>) : {};
    } catch {
      domains = {};
    }

    return [location.hostname, ...Object.keys(domains)];
  });

  const options = computed<LocalizeInternalUrlOptionsType>(() => ({
    locale: currentLocale.value,
    defaultLocale: defaultLocale.value,
    knownLocales: [...new Set([...supportedLocales.value, ...OVERLAY_LOCALES])],
    overlayLocales: OVERLAY_LOCALES,
    sameSiteHostnames: sameSiteHostnames.value,
  }));

  const localizeCmsUrl = (url: string): string => localizeInternalUrl(url, options.value);

  const localizeSoftwarePageUrl = (url: string): string =>
    localizeInternalUrl(url, { ...options.value, pathFilter: SOFTWARE_PAGES_PATH });

  return { localizeCmsUrl, localizeSoftwarePageUrl };
}
